import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const projects = {
    'e-commerce-platform': {
      title: 'E-Commerce Platform',
      category: 'Web Development',
      image: 'https://images.unsplash.com/photo-1557821552-17105176677c?w=1200&h=800&fit=crop',
      description: 'A full-featured e-commerce platform with AI-powered recommendations and real-time analytics.',
      client: 'RetailMax Inc.',
      duration: '6 Months',
      year: '2024',
      technologies: ['React', 'Node.js', 'MongoDB', 'AWS', 'Stripe', 'Redis'],
      features: [
        'AI-powered product recommendations',
        'Real-time inventory management',
        'Multi-currency support',
        'Advanced analytics dashboard',
        'Mobile-responsive design',
        'Secure payment integration'
      ],
      results: [
        { metric: '300%', label: 'Increase in Sales' },
        { metric: '50%', label: 'Reduced Cart Abandonment' },
        { metric: '2M+', label: 'Monthly Visitors' },
        { metric: '99.9%', label: 'Uptime' }
      ],
      testimonial: {
        text: "NestedInfo transformed our online presence completely. The new platform exceeded all our expectations.",
        author: "John Smith",
        role: "CEO, RetailMax Inc."
      },
      gallery: [
        'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1556742111-a301076d9d18?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1556740758-90de374c12ad?w=600&h=400&fit=crop'
      ]
    },
    'finance-mobile-app': {
      title: 'Finance Mobile App',
      category: 'Mobile Development',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop',
      description: 'Secure financial management app with biometric authentication and real-time updates.',
      client: 'FinSecure Bank',
      duration: '8 Months',
      year: '2024',
      technologies: ['React Native', 'TypeScript', 'Firebase', 'Plaid API', 'Face ID', 'Touch ID'],
      features: [
        'Biometric authentication',
        'Real-time transaction alerts',
        'Budget tracking & insights',
        'Bill payment automation',
        'Investment portfolio tracking',
        'Secure document storage'
      ],
      results: [
        { metric: '500K+', label: 'App Downloads' },
        { metric: '4.8★', label: 'App Store Rating' },
        { metric: '60%', label: 'Increased User Engagement' },
        { metric: '0', label: 'Security Breaches' }
      ],
      testimonial: {
        text: "The app has revolutionized how our customers manage their finances. Security and UX are top-notch.",
        author: "Sarah Johnson",
        role: "CTO, FinSecure Bank"
      },
      gallery: [
        'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop'
      ]
    },
    'healthcare-dashboard': {
      title: 'Healthcare Dashboard',
      category: 'Data Analytics',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&h=800&fit=crop',
      description: 'Comprehensive healthcare analytics dashboard with interactive visualizations.',
      client: 'MedCare Hospitals',
      duration: '5 Months',
      year: '2023',
      technologies: ['Vue.js', 'D3.js', 'Python', 'TensorFlow', 'PostgreSQL', 'Docker'],
      features: [
        'Real-time patient monitoring',
        'Predictive health analytics',
        'Resource allocation optimization',
        'Appointment scheduling system',
        'Medical records management',
        'Compliance reporting'
      ],
      results: [
        { metric: '40%', label: 'Reduced Wait Times' },
        { metric: '25%', label: 'Cost Savings' },
        { metric: '95%', label: 'Staff Satisfaction' },
        { metric: '100%', label: 'HIPAA Compliant' }
      ],
      testimonial: {
        text: "This dashboard has streamlined our operations and improved patient care significantly.",
        author: "Dr. Michael Chen",
        role: "Medical Director, MedCare"
      },
      gallery: [
        'https://images.unsplash.com/photo-1551076805-e1869033e561?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=600&h=400&fit=crop'
      ]
    },
    'smart-home-system': {
      title: 'Smart Home System',
      category: 'IoT Solutions',
      image: 'https://images.unsplash.com/photo-1558002038-1092e50bb7b6?w=1200&h=800&fit=crop',
      description: 'AI-powered smart home automation system with voice control and energy monitoring.',
      client: 'HomeAI Technologies',
      duration: '10 Months',
      year: '2023',
      technologies: ['Python', 'TensorFlow', 'Raspberry Pi', 'MQTT', 'React', 'Alexa SDK'],
      features: [
        'Voice-controlled automation',
        'Energy consumption analytics',
        'Security camera integration',
        'Climate control optimization',
        'Automated lighting scenes',
        'Remote access & monitoring'
      ],
      results: [
        { metric: '35%', label: 'Energy Savings' },
        { metric: '100K+', label: 'Devices Connected' },
        { metric: '24/7', label: 'Monitoring' },
        { metric: '4.9★', label: 'Customer Rating' }
      ],
      testimonial: {
        text: "Our smart home system is now the market leader thanks to NestedInfo's innovative solutions.",
        author: "Emily Davis",
        role: "Founder, HomeAI"
      },
      gallery: [
        'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1585128792020-803d29415281?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&h=400&fit=crop'
      ]
    },
    'education-platform': {
      title: 'Education Platform',
      category: 'Web Development',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&h=800&fit=crop',
      description: 'Interactive learning platform with AI tutoring and personalized curriculum.',
      client: 'EduTech Global',
      duration: '7 Months',
      year: '2024',
      technologies: ['Next.js', 'OpenAI API', 'Prisma', 'WebRTC', 'Stripe', 'Vercel'],
      features: [
        'AI-powered tutoring system',
        'Personalized learning paths',
        'Live video classes',
        'Progress tracking & analytics',
        'Gamification elements',
        'Certificate generation'
      ],
      results: [
        { metric: '1M+', label: 'Active Students' },
        { metric: '85%', label: 'Course Completion Rate' },
        { metric: '50+', label: 'Countries Reached' },
        { metric: '200%', label: 'Revenue Growth' }
      ],
      testimonial: {
        text: "The platform has transformed online education. Student engagement has never been higher.",
        author: "Prof. Robert Wilson",
        role: "CEO, EduTech Global"
      },
      gallery: [
        'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=600&h=400&fit=crop'
      ]
    },
    'logistics-tracker': {
      title: 'Logistics Tracker',
      category: 'Mobile Development',
      image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1200&h=800&fit=crop',
      description: 'Real-time logistics tracking app with predictive delivery analytics.',
      client: 'SwiftShip Logistics',
      duration: '6 Months',
      year: '2023',
      technologies: ['Flutter', 'Google Maps API', 'Node.js', 'MongoDB', 'Socket.io', 'ML Kit'],
      features: [
        'Real-time GPS tracking',
        'Predictive delivery times',
        'Route optimization',
        'Driver performance analytics',
        'Customer notifications',
        'Proof of delivery capture'
      ],
      results: [
        { metric: '45%', label: 'Faster Deliveries' },
        { metric: '30%', label: 'Fuel Cost Reduction' },
        { metric: '10K+', label: 'Daily Deliveries' },
        { metric: '98%', label: 'On-Time Delivery' }
      ],
      testimonial: {
        text: "This app has completely transformed our logistics operations. Efficiency is at an all-time high.",
        author: "Mark Anderson",
        role: "Operations Head, SwiftShip"
      },
      gallery: [
        'https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=600&h=400&fit=crop',
        'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop'
      ]
    }
  };

  const project = projects[projectId];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [projectId]);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Not Found</h1>
          <p className="text-gray-600 mb-8">The project you're looking for doesn't exist.</p>
          <Link 
            to="/portfolio"
            className="bg-green-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-600 transition-all"
          >
            Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition pt-20">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px]">
        <img 
          src={project.image} 
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-7xl mx-auto">
            <span className="inline-block bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {project.category}
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-4">{project.title}</h1>
            <p className="text-xl text-gray-200 max-w-2xl">{project.description}</p>
          </div>
        </div>
      </section>

      {/* Project Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            <div className="text-center p-6 bg-gray-50 rounded-2xl">
              <div className="text-sm text-gray-500 mb-2">Client</div>
              <div className="text-xl font-bold text-gray-900">{project.client}</div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-2xl">
              <div className="text-sm text-gray-500 mb-2">Duration</div>
              <div className="text-xl font-bold text-gray-900">{project.duration}</div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-2xl">
              <div className="text-sm text-gray-500 mb-2">Year</div>
              <div className="text-xl font-bold text-gray-900">{project.year}</div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-2xl">
              <div className="text-sm text-gray-500 mb-2">Category</div>
              <div className="text-xl font-bold text-gray-900">{project.category}</div>
            </div>
          </div>

          {/* Technologies */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Technologies Used</h2>
            <div className="flex flex-wrap gap-3">
              {project.technologies.map((tech, index) => (
                <span 
                  key={index}
                  className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-5 py-2 rounded-full font-medium"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Features</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                    <i className="fas fa-check"></i>
                  </div>
                  <span className="text-gray-700 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Results */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Results & Impact</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {project.results.map((result, index) => (
                <div key={index} className="text-center p-6 bg-gradient-to-br from-green-500 to-blue-600 rounded-2xl text-white">
                  <div className="text-4xl font-black mb-2">{result.metric}</div>
                  <div className="text-green-100">{result.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Gallery */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Gallery</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {project.gallery.map((img, index) => (
                <img 
                  key={index}
                  src={img} 
                  alt={`${project.title} gallery ${index + 1}`}
                  className="w-full h-64 object-cover rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
                />
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="mb-16">
            <div className="bg-gray-900 text-white p-10 rounded-3xl">
              <div className="text-5xl text-green-500 mb-4">
                <i className="fas fa-quote-left"></i>
              </div>
              <p className="text-2xl font-medium mb-6 leading-relaxed">"{project.testimonial.text}"</p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center text-2xl font-bold">
                  {project.testimonial.author.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-lg">{project.testimonial.author}</div>
                  <div className="text-gray-400">{project.testimonial.role}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to={`/case-study/${projectId}`}
              className="magnetic-btn bg-green-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-600 transition-all shadow-lg hover:shadow-green-500/30 flex items-center justify-center gap-2"
            >
              View Full Case Study
              <i className="fas fa-arrow-right"></i>
            </Link>
            <Link 
              to="/portfolio"
              className="bg-gray-100 text-gray-800 px-8 py-4 rounded-full font-semibold hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
            >
              <i className="fas fa-arrow-left"></i>
              Back to Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-500 to-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-3xl md:text-5xl font-black mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-green-100 mb-8">Let's create something amazing together</p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-green-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition-all shadow-lg"
          >
            Get In Touch
            <i className="fas fa-arrow-right"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default ProjectDetailPage;